/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React from 'react';
import { SvgIcon, SvgIconProps } from '@material-ui/core';

/**
 * The knowledge-graph mark (V5, "path"): a traversal with a fork — the
 * reviewer's blast-radius walk — deepened into a 3D cage by a smaller back
 * layer at reduced opacity (size + weight as the depth cues). Original
 * geometry on the marketplace brand logic: one stroke (1.6), node
 * vocabulary, color always inherited so accents, themes and selection
 * states come free. `solid` fills all nodes (nav, standalone); `duo` keeps
 * the origin filled and rings the rest (rail rows, section bands), edges
 * trimmed to ring boundaries so open nodes stay open.
 */
type Props = SvgIconProps & { variant?: 'solid' | 'duo' };

const BACK_OPACITY = 0.55;

export const KgNodesIcon = ({ variant = 'solid', ...props }: Props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    {variant === 'solid' ? (
      <>
        <g opacity={BACK_OPACITY} stroke="currentColor" strokeWidth={1.6} fill="none">
          <path d="M4.5 18.8L6.6 6.6M6.6 6.6L16.6 4.4M16.6 4.4L20.6 8.8M20.6 8.8L19.4 15.6" />
        </g>
        <path
          d="M4.5 18.8L11.6 11.2M11.6 11.2L19.4 15.6M11.6 11.2L16.6 4.4"
          stroke="currentColor"
          strokeWidth={1.6}
          fill="none"
        />
        <g opacity={BACK_OPACITY}>
          <circle cx={6.6} cy={6.6} r={1.6} />
          <circle cx={20.6} cy={8.8} r={1.5} />
        </g>
        <circle cx={4.5} cy={18.8} r={2.5} />
        <circle cx={11.6} cy={11.2} r={2.4} />
        <circle cx={19.4} cy={15.6} r={2.3} />
        <circle cx={16.6} cy={4.4} r={2.2} />
      </>
    ) : (
      <>
        <g opacity={BACK_OPACITY} stroke="currentColor" strokeWidth={1.6} fill="none">
          <path d="M4.5 18.8L6.19 8.97M8.94 6.08L13.67 5.04M18.62 6.62L19.05 7.1M20.2 11.07L19.94 12.55" />
          <circle cx={6.6} cy={6.6} r={1.6} />
          <circle cx={20.6} cy={8.8} r={1.5} />
        </g>
        <path
          d="M4.5 18.8L9.42 13.54M14.39 12.77L16.7 14.08M13.5 8.62L14.82 6.82"
          stroke="currentColor"
          strokeWidth={1.6}
          fill="none"
        />
        <circle cx={4.5} cy={18.8} r={2.5} />
        <g fill="none" stroke="currentColor" strokeWidth={1.6}>
          <circle cx={11.6} cy={11.2} r={2.4} />
          <circle cx={19.4} cy={15.6} r={2.3} />
          <circle cx={16.6} cy={4.4} r={2.2} />
        </g>
      </>
    )}
  </SvgIcon>
);

/** Zero-config solid form for Backstage's SidebarItem icon slot. */
export const KgNodesNavIcon = (props: SvgIconProps) => (
  <KgNodesIcon variant="solid" {...props} />
);
